import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Goal } from '@/types/experiment';
import { Loader2, Target } from 'lucide-react';

interface CreateGoalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateGoal: (goal: Goal) => void;
  propelauth: any;
}

export const CreateGoalDialog: React.FC<CreateGoalDialogProps> = ({
  open,
  onOpenChange,
  onCreateGoal,
  propelauth
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    success_metric: '',
    target_value: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get user ID from PropelAuth
  const getUserId = async () => {
    try {
      const authInfo = await propelauth.getAuthenticationInfoOrNull();
      return authInfo?.user?.userId || null;
    } catch (error) {
      console.error('Error getting user ID:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.success_metric.trim()) {
      setError('Name and success metric are required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Get user ID properly
      const userId = await getUserId();
      if (!userId) {
        setError('Authentication required. Please refresh and try again.');
        return;
      }

      // Get auth headers
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'x-user-id': userId,
      };

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        success_metric: formData.success_metric.trim(),
        target_value: formData.target_value ? parseFloat(formData.target_value) : undefined,
      };

      const response = await fetch('/api/goals', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        // Convert API response to match Goal interface
        const newGoal: Goal = {
          ...data.goal,
          createdAt: new Date(data.goal.created_at),
          experiment_count: 0 // New goals start with 0 experiments
        };
        onCreateGoal(newGoal);
        setFormData({ name: '', description: '', success_metric: '', target_value: '' });
        onOpenChange(false);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to create goal');
      }
    } catch (error) {
      console.error('Error creating goal:', error);
      setError('Failed to create goal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null); // Clear error when user starts typing
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ name: '', description: '', success_metric: '', target_value: '' });
      setError(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Create New Goal
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="goalName">Goal Name *</Label>
            <Input
              id="goalName"
              value={formData.name}
              onChange={handleChange('name')}
              placeholder="e.g., Improve Model Accuracy"
              required
              disabled={isSubmitting}
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={handleChange('description')}
              placeholder="What is this goal about? (optional)"
              rows={2}
              disabled={isSubmitting}
            />
          </div>
          
          <div>
            <Label htmlFor="successMetric">Success Metric *</Label>
            <Input
              id="successMetric"
              value={formData.success_metric}
              onChange={handleChange('success_metric')}
              placeholder="e.g., accuracy, F1-score, precision"
              required
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground mt-1">
              How will you measure success for this goal?
            </p>
          </div>
          
          <div>
            <Label htmlFor="targetValue">Target Value</Label>
            <Input
              id="targetValue"
              type="number"
              step="any"
              value={formData.target_value}
              onChange={handleChange('target_value')}
              placeholder="e.g., 0.95, 85, 1000"
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Target value for your success metric (optional)
            </p>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={!formData.name.trim() || !formData.success_metric.trim() || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Goal'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 